import { useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import type { ChatMessage } from "../state/chat";
import { ToolEventCard } from "./ToolEventCard";

interface MessageListProps {
  messages: ChatMessage[];
  isLoading: boolean;
}

export function MessageList({ messages, isLoading }: MessageListProps) {
  const bottomRef = useRef<HTMLDivElement | null>(null);

  // Re-derive scroll signal from message count + the last message's text
  // length so we tick during streaming as well, not just when a new message
  // is appended. Without this the second turn appears "below the fold" and
  // the user thinks there's nowhere to continue.
  const last = messages[messages.length - 1];
  const tickKey = `${messages.length}:${last?.id ?? ""}:${last?.text.length ?? 0}:${last?.toolEvents.length ?? 0}`;
  useEffect(() => {
    const node = bottomRef.current;
    if (!node) return;
    // Use rAF so the DOM has flushed the new chunk before we scroll;
    // otherwise on Safari the bottom anchor lags one frame behind.
    const id = requestAnimationFrame(() => {
      node.scrollIntoView({ block: "end", behavior: "auto" });
    });
    return () => cancelAnimationFrame(id);
  }, [tickKey]);

  if (isLoading) {
    return (
      <div className="empty-state">
        <div className="empty-icon">...</div>
        <h2>正在读取历史</h2>
        <p>稍等一下，我在恢复这个会话。</p>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-icon">D</div>
        <h2>问车大师一个问题</h2>
        <p>比如：帮我评估 BRZ 原厂底盘，或分析换半热熔后悬架和定位需要怎么补。</p>
        <div className="prompt-suggestions">
          <span>评估一台车</span>
          <span>分析改装方案</span>
          <span>生成阶段计划</span>
        </div>
      </div>
    );
  }

  return (
    <div className="message-list">
      {messages.map((message) => (
        <article key={message.id} className={`message ${message.role}`}>
          <div className="message-avatar">{message.role === "assistant" ? "D" : "你"}</div>
          <div className="message-bubble">
            {message.thoughts.length > 0 ? (
              <details className="thoughts">
                <summary>思考过程</summary>
                <ReactMarkdown>{message.thoughts.join("")}</ReactMarkdown>
              </details>
            ) : null}

            {message.toolEvents.map((group) => (
              <ToolEventCard key={group.id} group={group} />
            ))}

            {message.text ? (
              <ReactMarkdown>{message.text}</ReactMarkdown>
            ) : message.isStreaming ? (
              <span className="typing-cursor">车大师正在思考...</span>
            ) : null}
          </div>
        </article>
      ))}
      <div ref={bottomRef} />
    </div>
  );
}
